import * as React from "react";
import type { Language } from "../i18n";
import { t } from "../i18n";

export interface CartItem {
  id: string;
  barcode: string;
  name?: string;
  price?: number;
  currency?: string;
  quantity?: number;
}

export type BatchItem = CartItem;

export interface CartBottomSheetProps {
  items: CartItem[];
  onRemove: (id: string) => void;
  onIncrementQuantity?: (id: string) => void;
  onDecrementQuantity?: (id: string) => void;
  onUpdateQuantity?: (id: string, quantity: number) => void;
  onClear?: () => void;
  onSubmit: () => void;
  language?: Language;
  defaultExpanded?: boolean;
  isOpen?: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
}

function hasReactDispatcher(): boolean {
  try {
    const r = React as any;
    return Boolean(
      r?.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE?.H ||
      r?.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentDispatcher?.current,
    );
  } catch {
    return false;
  }
}

function useSafeState<T>(initial: T): [T, (val: T | ((prev: T) => T)) => void] {
  if (!hasReactDispatcher()) {
    return [initial, () => {}];
  }
  try {
    return React.useState<T>(initial);
  } catch {
    return [initial, () => {}];
  }
}

function useSafeRef<T>(initial: T): { current: T } {
  if (!hasReactDispatcher()) {
    return { current: initial };
  }
  try {
    return React.useRef<T>(initial);
  } catch {
    return { current: initial };
  }
}

export function CartBottomSheet({
  items,
  onRemove,
  onIncrementQuantity,
  onDecrementQuantity,
  onUpdateQuantity,
  onClear,
  onSubmit,
  language = "ru",
  defaultExpanded = true,
  isOpen: controlledIsOpen,
  onToggleOpen,
}: CartBottomSheetProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useSafeState<boolean>(defaultExpanded);
  const isExpanded = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;

  const setIsExpanded = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === "function" ? next(isExpanded) : next;
    if (controlledIsOpen === undefined) {
      setUncontrolledIsOpen(resolved);
    }
    onToggleOpen?.(resolved);
  };

  // Touch swipe gesture handlers
  const touchStartYRef = useSafeRef<number | null>(null);
  const touchDeltaYRef = useSafeRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY;
    touchDeltaYRef.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartYRef.current === null) return;
    const currentY = e.touches[0].clientY;
    touchDeltaYRef.current = currentY - touchStartYRef.current;
  };

  const handleTouchEnd = () => {
    if (touchStartYRef.current === null) return;
    const deltaY = touchDeltaYRef.current;
    const SWIPE_THRESHOLD = 30;

    if (deltaY < -SWIPE_THRESHOLD && !isExpanded) {
      // Swiped UP -> expand
      setIsExpanded(true);
    } else if (deltaY > SWIPE_THRESHOLD && isExpanded) {
      // Swiped DOWN -> collapse
      setIsExpanded(false);
    }
    touchStartYRef.current = null;
    touchDeltaYRef.current = 0;
  };

  // Calculations
  const totalCount = items.reduce((acc, item) => acc + (item.quantity ?? 1), 0);
  const totalPrice = items.reduce(
    (acc, item) => acc + (item.price !== undefined ? item.price * (item.quantity ?? 1) : 0),
    0,
  );
  const hasAnyPrice = items.some((item) => item.price !== undefined);
  const currencySymbol = items.find((item) => item.currency)?.currency || "€";

  const formattedTotalPrice = hasAnyPrice
    ? `${totalPrice.toFixed(2)} ${currencySymbol}`
    : `0.00 ${currencySymbol}`;

  const submitText =
    totalCount > 0
      ? t(language, "batch_submit_count", { count: totalCount })
      : t(language, "batch_submit_btn");

  const handleIncrement = (id: string) => {
    if (onIncrementQuantity) {
      onIncrementQuantity(id);
    } else if (onUpdateQuantity) {
      const item = items.find((i) => i.id === id);
      onUpdateQuantity(id, (item?.quantity ?? 1) + 1);
    }
  };

  const handleDecrement = (id: string) => {
    if (onDecrementQuantity) {
      onDecrementQuantity(id);
    } else if (onUpdateQuantity) {
      const item = items.find((i) => i.id === id);
      const currentQty = item?.quantity ?? 1;
      if (currentQty > 1) {
        onUpdateQuantity(id, currentQty - 1);
      }
    }
  };

  return (
    <div
      className={`cart-bottom-sheet batch-panel ${
        isExpanded
          ? "cart-bottom-sheet--expanded batch-panel--expanded"
          : "cart-bottom-sheet--collapsed batch-panel--collapsed"
      }`}
      data-testid="batch-panel"
      aria-expanded={isExpanded}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="cart-bottom-sheet__handle-bar batch-panel__header"
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        aria-label={isExpanded ? t(language, "cart_collapse_aria") : t(language, "cart_expand_aria")}
        data-testid="cart-header"
      >
        <div className="cart-bottom-sheet__pill" data-testid="cart-drag-handle" />
        <div className="cart-bottom-sheet__header-content">
          <div className="cart-bottom-sheet__title batch-panel__title">
            <span>{t(language, "batch_title")}</span>
            <span
              className="cart-bottom-sheet__badge batch-panel__badge"
              data-testid="batch-count"
            >
              {totalCount}
            </span>
          </div>

          <div className="cart-bottom-sheet__header-right">
            <span className="cart-bottom-sheet__header-sum" data-testid="cart-header-sum">
              {formattedTotalPrice}
            </span>
            {items.length > 0 && onClear && (
              <button
                type="button"
                className="batch-panel__clear-btn cart-bottom-sheet__clear-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                aria-label={t(language, "batch_clear_aria")}
                data-testid="batch-clear-btn"
              >
                {t(language, "batch_clear_btn")}
              </button>
            )}
            <button
              type="button"
              className="cart-bottom-sheet__toggle-btn"
              aria-label={isExpanded ? t(language, "cart_collapse_aria") : t(language, "cart_expand_aria")}
              data-testid="cart-toggle-btn"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`cart-bottom-sheet__chevron ${
                  isExpanded ? "cart-bottom-sheet__chevron--expanded" : ""
                }`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="cart-bottom-sheet__body">
        {items.length === 0 ? (
          <div className="batch-panel__empty cart-bottom-sheet__empty" data-testid="batch-empty">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="batch-panel__empty-icon"
            >
              <path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2z" />
              <path d="M7 9h10" />
              <path d="M7 13h10" />
              <path d="M7 17h6" />
            </svg>
            <span>{t(language, "batch_empty_hint")}</span>
          </div>
        ) : (
          <ul className="batch-panel__list cart-bottom-sheet__list" data-testid="batch-list">
            {items.map((item, index) => {
              const qty = item.quantity ?? 1;
              const hasPrice = item.price !== undefined;
              const unitPriceStr = hasPrice
                ? `${item.price!.toFixed(2)} ${item.currency || "€"}`
                : null;
              const itemTotalStr = hasPrice
                ? `${(item.price! * qty).toFixed(2)} ${item.currency || "€"}`
                : null;

              return (
                <li
                  key={item.id}
                  className="batch-panel__item cart-bottom-sheet__item"
                  data-testid={`cart-item-${item.id}`}
                >
                  <span className="batch-panel__item-index">{`#${index + 1}`}</span>
                  <div className="batch-panel__item-content cart-bottom-sheet__item-details">
                    <span className="batch-panel__item-code">{item.barcode}</span>
                    {item.name && (
                      <span className="batch-panel__item-name cart-bottom-sheet__item-name">
                        {item.name}
                      </span>
                    )}
                    {hasPrice && (
                      <span
                        className="cart-bottom-sheet__item-price"
                        data-testid="cart-item-price"
                      >
                        {qty > 1 ? `${qty} × ${unitPriceStr} = ${itemTotalStr}` : unitPriceStr}
                      </span>
                    )}
                  </div>

                  <div
                    className="cart-bottom-sheet__quantity-stepper"
                    data-testid="cart-quantity-stepper"
                  >
                    <button
                      type="button"
                      className={`cart-bottom-sheet__stepper-btn cart-bottom-sheet__stepper-btn--dec ${
                        qty <= 1 ? "cart-bottom-sheet__stepper-btn--min" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (qty > 1) {
                          handleDecrement(item.id);
                        }
                      }}
                      aria-label={t(language, "cart_decrease_qty")}
                      data-testid={`cart-qty-decrease-${item.id}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                    <span
                      className="cart-bottom-sheet__stepper-val"
                      data-testid={`cart-qty-value-${item.id}`}
                    >
                      {qty}
                    </span>
                    <button
                      type="button"
                      className="cart-bottom-sheet__stepper-btn cart-bottom-sheet__stepper-btn--inc"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleIncrement(item.id);
                      }}
                      aria-label={t(language, "cart_increase_qty")}
                      data-testid={`cart-qty-increase-${item.id}`}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </button>
                  </div>

                  <button
                    type="button"
                    className="batch-panel__item-remove cart-bottom-sheet__item-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    aria-label={t(language, "batch_remove_aria", { barcode: item.barcode })}
                    title={t(language, "batch_remove_title")}
                    data-testid={`cart-remove-${item.id}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="cart-bottom-sheet__footer batch-panel__actions">
          {items.length > 0 && (
            <div className="cart-bottom-sheet__summary-row" data-testid="cart-summary-row">
              <span className="cart-bottom-sheet__summary-label">{t(language, "cart_total")}:</span>
              <span
                className="cart-bottom-sheet__summary-value"
                data-testid="cart-total-sum"
              >
                {formattedTotalPrice}
              </span>
            </div>
          )}
          <button
            type="button"
            className="batch-panel__submit-btn cart-bottom-sheet__submit-btn"
            disabled={items.length === 0}
            onClick={onSubmit}
            data-testid="batch-submit-btn"
          >
            {submitText}
          </button>
        </div>
      </div>
    </div>
  );
}
